import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import Loading from "~/components/Loading";
import { ManifoldNextGenAppHeader } from "~/components/ManifoldNextGenAppHeader";
import { NextGenSwitchPrompt } from "~/components/NextGenSwitchPrompt";
import { NextGenHeaderProvider } from "~/contexts/nextgen-header";
import { PageContextProvider } from "~/contexts/page-context";
import {
  useForceManifoldNextGenFeature,
  useManifoldNextGenFeature,
} from "~/hooks/useManifoldNextGenFeature";
import { useNextGenTheme, type NextGenTheme } from "~/hooks/useNextGenTheme";
import { useUserPreferences } from "~/hooks/useUserPreferences";
import { ROUTE_404 } from "~/router/constants";

export interface NextGenLayoutContext extends NextGenTheme {
  // Undefined when the user is locked into the new experience (no legacy fallback to switch to).
  onSwitchToClassic?: () => void;
}

export function ManifoldNextGenLayout() {
  const feature = useManifoldNextGenFeature();
  const forceFeature = useForceManifoldNextGenFeature();
  const [preferences, updatePreferences] = useUserPreferences();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useNextGenTheme();

  // The pathname this layout instance first rendered at. This layout mounts
  // exactly once when the user enters the /nextgen/* route tree and never
  // remounts while navigating between nextgen pages, so a change away from
  // that pathname means the user actually navigated within nextgen — as
  // opposed to landing here directly (typed URL, bookmark, refresh, or a link
  // from outside nextgen), where there's no in-app "back" destination.
  const [initialPathname] = useState(() => location.pathname);
  const [canGoBack, setCanGoBack] = useState(false);

  useEffect(() => {
    if (location.pathname !== initialPathname) {
      setCanGoBack(true);
    }
  }, [location.pathname, initialPathname]);

  useEffect(() => {
    document.body.dataset.theme = "manifold-nextgen";
    return () => {
      delete document.body.dataset.theme;
    };
  }, []);

  if (feature.isLoading || forceFeature.isLoading) {
    return <Loading />;
  }

  if (!feature.enabled && !forceFeature.enabled) {
    return <Navigate to={ROUTE_404} replace />;
  }

  const isLockedIntoNextGen = forceFeature.enabled;

  // Eligible for the new experience but not yet opted in: offer to switch
  // rather than dead-ending the link at a 404. Users locked into the new
  // experience skip this prompt entirely — there is no legacy fallback for them.
  if (!isLockedIntoNextGen && !preferences.optedIntoManifoldNextGen) {
    return (
      <NextGenSwitchPrompt
        onConfirm={() => updatePreferences({ optedIntoManifoldNextGen: true })}
        onCancel={() => navigate(ROUTE_404, { replace: true })}
      />
    );
  }

  const handleSwitchToClassic = () => {
    updatePreferences({ optedIntoManifoldNextGen: false });
    navigate("/");
  };

  const onSwitchToClassic = isLockedIntoNextGen ? undefined : handleSwitchToClassic;

  return (
    <NextGenHeaderProvider>
      <PageContextProvider>
        <QueryParamProvider adapter={ReactRouter6Adapter} options={{ removeDefaultsFromUrl: true }}>
          <div className="flex h-screen flex-col bg-background text-foreground">
            <ManifoldNextGenAppHeader onSwitchToClassic={onSwitchToClassic} canGoBack={canGoBack} />
            <main className="flex-1 overflow-auto">
              <Outlet context={{ ...theme, onSwitchToClassic } satisfies NextGenLayoutContext} />
            </main>
          </div>
        </QueryParamProvider>
      </PageContextProvider>
    </NextGenHeaderProvider>
  );
}
